/**
 * 仮の雲（docs/05_rendering-and-audio.md 5 章）：ふわっとした塊をインスタンス描画する。
 * 下端を雲底でそろえるので、景色から雲底の高さが読める。雷雨の前触れでは高く積み上がり、暗くなる。
 */
import * as THREE from 'three';
import { hash2, smoothstep } from '../../core/math';
import type { Cloud, Storm } from '../../sim/weather/model';

const MAX_PUFFS = 8000;
const PUFFS_PER_CLOUD = 6;

export class CloudField {
  readonly mesh: THREE.InstancedMesh;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly s = new THREE.Vector3();
  private readonly p = new THREE.Vector3();
  private readonly c = new THREE.Color();
  private readonly white = new THREE.Color(0xf7f9fc);
  private readonly dark = new THREE.Color(0x5b6472);

  constructor() {
    const geo = new THREE.IcosahedronGeometry(1, 2);
    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 });
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_PUFFS);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
  }

  private put(i: number, x: number, y: number, z: number, r: number, flat: number, color: THREE.Color): void {
    this.p.set(x, y, z);
    this.s.set(r, r * flat, r);
    this.m.compose(this.p, this.q, this.s);
    this.mesh.setMatrixAt(i, this.m);
    this.mesh.setColorAt(i, color);
  }

  update(clouds: Cloud[], storms: Storm[], timeOfDayS: number): void {
    let i = 0;
    for (const cl of clouds) {
      if (i + PUFFS_PER_CLOUD + 12 >= MAX_PUFFS) break;
      const grow =
        smoothstep(cl.bornS, cl.bornS + 240, timeOfDayS) *
        (1 - smoothstep(cl.dieS - 300, cl.dieS, timeOfDayS));
      if (grow <= 0.02) continue;
      const size = cl.sizeM * (0.4 + 0.6 * grow);
      this.c.copy(this.white).lerp(this.dark, cl.towering * 0.8);
      for (let k = 0; k < PUFFS_PER_CLOUD; k++) {
        const hx = hash2(cl.id, k, 1) - 0.5;
        const hz = hash2(cl.id, k, 2) - 0.5;
        const hr = 0.35 + 0.25 * hash2(cl.id, k, 3);
        const r = size * hr;
        const y = cl.baseM + r * 0.55 + size * 0.25 * hash2(cl.id, k, 4);
        this.put(i++, cl.x + hx * size, y, cl.z + hz * size, r, 0.6, this.c);
      }
      // 雷雨の前触れ：高く積み上がる
      if (cl.towering > 0.05) {
        const top = cl.topM;
        const step = size * 0.8;
        for (let y = cl.baseM + size; y < top && i < MAX_PUFFS - 1; y += step) {
          const r = size * (0.5 + 0.1 * hash2(cl.id, Math.round(y), 5));
          this.put(i++, cl.x + (hash2(cl.id, Math.round(y), 6) - 0.5) * size * 0.4, y, cl.z, r, 0.9, this.c);
        }
      }
    }
    // 成熟した雷雲：大きな暗い柱と金床
    for (const st of storms) {
      if ((st.phase !== 'mature' && st.phase !== 'dissipating') || st.radiusM <= 0) continue;
      const r = st.radiusM * 0.45;
      this.c.copy(this.dark);
      for (let y = 3000; y < 9000 && i < MAX_PUFFS - 1; y += r * 0.7) {
        const spread = y > 7500 ? 1.8 : 1;
        this.put(i++, st.x, y, st.z, r * spread, 0.7, this.c);
      }
    }
    this.mesh.count = i;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}
